import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { Container, Button, Modal } from "react-bootstrap";
import moment from "moment";
import { AllInclusive } from "@mui/icons-material"; // ⟵ for infinity

import deliverySlotApi from "../../services/deliverySlotApi";
import branchApi from "../../services/branchapi";

import Loader from "../loader/loader";
import SearchableDropdown from "../components/SearchableDropdown";
import { showConfirm } from "react-confirm-prompt";
import DynamicBreadcrumb from "../header/breadcrumb";
import DeliverySlotForm from "./DeliverySlotForm";

const currency = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const DeliverySlotList = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });

    const [totalRecords, setTotalRecords] = useState(0);
    const [branchId, setBranchID] = useState(0);
    const [branchName, setBranchName] = useState("All");
    const [allBranches, setAllBranches] = useState([]);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);

    // Slot Form State
    const [openFormModal, setOpenFormModal] = useState(false);
    const [formData, setFormData] = useState(null); // for edit

    const pathArray = [{ name: "Delivery Slots", url: null }];

    const fetchBranches = async () => {
        try {
            const response = await branchApi.getAllBranches({
                limit: 100,
                page: 0,
                status: 1,
                sortModel: [{ field: "branchName", sort: "asc" }],
            });
            const branches = response.data.result;
            const sortedBranches = branches.sort((a, b) => a.branchName.localeCompare(b.branchName));
            setAllBranches(sortedBranches);
        } catch (error) {
            console.error("Failed to fetch branches", error);
        }
    };

    const fetchDeliverySlots = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;

            const response = await deliverySlotApi.getDeliverySlots({
                limit: pageSize,
                page,
                sortModel,
                filterModel,
                branchId,
            });

            const formattedRows = response.data.result.map((slot) => {
                // Raw times (assuming API provides "HH:mm")
                const from24 = slot.fromTime;
                const to24 = slot.toTime;

                // Display times
                const fromDisp = moment(from24, "HH:mm").format("hh:mm A");
                const toDisp = moment(to24, "HH:mm").format("hh:mm A");

                // Amounts
                const minAmount = slot.minAmount ?? 0;
                const maxAmount = slot.maxAmount ?? 0;

                return {
                    id: slot.id,
                    branch: slot.deliveryBranch?.branchName || "Main Branch",
                    branch_id: slot.deliveryBranch?.branchId,
                    // keep raw & display times for edit & view
                    fromTime: fromDisp,
                    toTime: toDisp,
                    fromTimeRaw: from24,
                    toTimeRaw: to24,
                    timeRange: `${fromDisp} – ${toDisp}`,
                    minAmount,
                    maxAmount,
                    threshold: slot.orderThreshold,
                    status: slot.status,
                    createdAt: moment(slot.createdDate).format("YYYY/MM/DD HH:mm"),
                    updatedAt: slot.updatedDate ? moment(slot.updatedDate).format("YYYY/MM/DD HH:mm") : "-",
                };
            });

            setRows(formattedRows);
            setTotalRecords(response.data.totalRecords[0]?.total_records || 0);
        } catch (error) {
            console.error("Error fetching delivery slots", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, branchId]);

    useEffect(() => {
        fetchBranches();
    }, []);

    useEffect(() => {
        fetchDeliverySlots();
    }, [fetchDeliverySlots]);

    const AmountRangeCell = ({ row }) => {
        if (!row) return null;
        const min = Number(row.minAmount) || 0;
        const max = Number(row.maxAmount) || 0;
        const hasMax = max > 0;
        return (
            <span>
                {currency.format(min)} – {hasMax ? currency.format(max) : <AllInclusive fontSize="small" style={{ verticalAlign: "middle" }} />}
            </span>
        );
    }

    const columns = [
        { field: "branch", headerName: "Branch", flex: 1, minWidth: 160 },
        { field: "timeRange", headerName: "Time Range", flex: 1, minWidth: 170 },
        {
            field: "amountRange",
            headerName: "Amount Range",
            flex: 1,
            minWidth: 190,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                if (!params || !params.row) return null;
                return <AmountRangeCell row={params.row} />;
            },
            valueGetter: (params) => {
                const row = params?.row || {};
                const min = Number(row.minAmount) || 0;
                const max = Number(row.maxAmount) || 0;
                return `${min}-${max || "INF"}`;
            },
        },
        { field: "threshold", headerName: "Order Threshold", flex: 1, minWidth: 150 },
        { field: "createdAt", headerName: "Created On", flex: 1, minWidth: 170 },
        { field: "updatedAt", headerName: "Updated On", flex: 1, minWidth: 170 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 130,
            renderCell: (params) => (
                <button
                    onClick={() => handleStatusClick(params.id, params.value)}
                    className={`btn ${parseInt(params.value) === 1 ? "btn-danger" : "btn-secondary"}`}
                >
                    {params.value === 1 ? "Active" : "Inactive"}
                </button>
            ),
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 160,
            renderCell: (params) => (
                <Box sx={{ flexDirection: "row" }} className="d-flex">
                    <button className="btn btn-sm btn-primary" onClick={() => handleView(params.row)}>
                        View
                    </button>
                    <button className="btn btn-sm btn-success mx-1" onClick={() => handleEdit(params.row)}>
                        Edit
                    </button>
                </Box>
            ),
        },
    ];

    const handleView = (slot) => {
        setSelectedSlot(slot);
        setOpenViewModal(true);
    };

    const handleEdit = (slotRow) => {
        const editData = {
            branch_id: slotRow.branch_id,
            slots: [
                {
                    id: slotRow.id,
                    // use raw 24h values if your form expects HH:mm; otherwise keep display
                    from_time: slotRow.fromTimeRaw || slotRow.fromTime,
                    to_time: slotRow.toTimeRaw || slotRow.toTime,
                    order_threshold: slotRow.threshold,
                    status: slotRow.status,
                    min_amount: slotRow.minAmount,
                    max_amount: slotRow.maxAmount,
                },
            ],
        };
        setFormData(editData);
        setOpenFormModal(true);
    };

    const handleStatusClick = (slotId, status) => {
        const msg = status === 1 ? "inactivate" : "activate";
        showConfirm(`Are you sure you want to ${msg} this slot?`, {
            type: "info",
        }).then(async (yes) => {
            if (yes) {
                try {
                    const newStatus = status === 1 ? 2 : 1;
                    await deliverySlotApi.updateSlotStatus({ slotId, status: newStatus });
                    fetchDeliverySlots();
                } catch (error) {
                    console.error("Status update failed", error);
                }
            }
        });
    };

    const handleFilterModelChange = (model) => {
        setFilterModel(model);
    };

    return (
        <Container className="mt-4 mb-4">
            <Box className="d-flex justify-content-between align-items-center mb-3">
                <DynamicBreadcrumb pathArray={pathArray} />
                <Button
                    variant="primary"
                    onClick={() => {
                        setFormData(null);
                        setOpenFormModal(true);
                    }}
                >
                    Add New Slot
                </Button>
            </Box>

            <Box className="mb-3 d-flex justify-content-end">
                <SearchableDropdown
                    options={allBranches}
                    extraoption={{ branchId: 0, branchName: "All" }}
                    label="branchName"
                    id="branchId"
                    selectedVal={branchName}
                    handleChange={({ id = 0, name = "All" }) => {
                        setBranchID(id);
                        setBranchName(name);
                    }}
                />
            </Box>

            {loading && <Loader />}
            <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                rowCount={totalRecords}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                onSortModelChange={setSortModel}
                onFilterModelChange={handleFilterModelChange}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: false,
                        printOptions: { disableToolbarButton: true },
                        csvOptions: { disableToolbarButton: true },
                    },
                }}
                sx={{
                    [`& .${gridClasses.cell}`]: {
                        py: 0.5,
                    },
                }}
            />

            <Modal show={openViewModal} onHide={() => setOpenViewModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>View Delivery Slot</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h6>Branch: {selectedSlot?.branch}</h6>
                    <h6>Time: {selectedSlot?.timeRange}</h6>
                    <h6>
                        Amount Range:{" "}
                        {selectedSlot
                            ? Number(selectedSlot.maxAmount) > 0
                                ? `${currency.format(selectedSlot.minAmount)} – ${currency.format(selectedSlot.maxAmount)}`
                                : `${currency.format(selectedSlot.minAmount)} – ∞`
                            : "-"}
                    </h6>
                    <h6>Order Threshold: {selectedSlot?.threshold}</h6>
                    <h6>Created: {selectedSlot?.createdAt}</h6>
                    <h6>Updated: {selectedSlot?.updatedAt}</h6>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setOpenViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <DeliverySlotForm
                show={openFormModal}
                onHide={() => setOpenFormModal(false)}
                initialData={formData}
                onSave={() => {
                    setOpenFormModal(false);
                    fetchDeliverySlots();
                }}
            />
        </Container>
    );
};

export default DeliverySlotList;
